import OpenAI from "openai"
import { PDFDocument } from 'pdf-lib'
import * as pdfjsLib from 'pdfjs-dist'
import { isBrowser } from "@/lib/agents/isbrowser"
import { documentContentExtractionPrompt } from './prompts'
import { chunkText, extractLocaleAndText, uploadFile } from "./utils"
const MAX_SIZE = 32 * 1024 * 1024 // 32MB


async function extractFromDocument(file: File, client: OpenAI) {

  const fileu = await uploadFile(file, client, "user_data")

  console.log("Calling OpenAI...")
  const response = await client.responses.create({
    model: "gpt-5-nano",
    input: [
      {
        role: "user",
        content: [
          {
            type: "input_file",
            file_id: fileu.id,
          },
          {
            type: "input_text",
            text: documentContentExtractionPrompt,
          },
        ],
      },
    ],
  })

  console.log("Received response from OpenAI.")

  await client.files.delete(fileu.id)
  const ext = extractLocaleAndText(response.output_text || "")
  return ext

}

export async function extractPdf(inputFile: File, apiKey: string) {

  const client = new OpenAI({ apiKey, dangerouslyAllowBrowser: true, baseURL: isBrowser ? "https://signpost-ia-app-qa.azurewebsites.net/decorsify/https://api.openai.com/v1" : undefined })

  console.log("Splitting file...")
  const files = await splitPdf(inputFile)
  console.log(`Splited files ${files.length}.`)

  const ext: ExtractedDocument = {
    locale: "",
    text: "",
  }

  for (const file of files) {
    console.log("uploading file to OpenAI...")
    const r = await extractFromDocument(file, client)
    if (!ext.locale && r.locale) ext.locale = r.locale
    ext.text += r.text + "\n\n"
  }

  ext.chunks = await chunkText(ext.text)

  return ext

}

async function splitPdf(file: File): Promise<File[]> {

  try {

    if (file.size <= MAX_SIZE) return [file]

    console.log(`PDF exceeds ${MAX_SIZE / (1024 * 1024)}MB, converting to JPG images at 75% quality...`)

    // Convert entire PDF to JPG images and create a new compressed PDF
    const compressedPdf = await convertPdfToJpgPdf(file)

    // Now split the compressed PDF into chunks
    console.log("Splitting the pdf file...")
    return await splitCompressedPdf(compressedPdf, file.name)

  } catch (error) {
    console.error('Error splitting PDF:', error)
    throw error
  }
}

async function convertPdfToJpgPdf(file: File): Promise<File> {

  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString()

  const buffer = await file.arrayBuffer()
  const loadingTask = pdfjsLib.getDocument({ data: buffer })
  const pdfDocument = await loadingTask.promise

  // Create a new PDF document
  const newPdfDoc = await PDFDocument.create()

  // Render all pages as JPG images
  for (let pageNum = 1; pageNum <= pdfDocument.numPages; pageNum++) {

    console.log(`Converting PDF page ${pageNum} / ${pdfDocument.numPages} to JPG...`)
    const page = await pdfDocument.getPage(pageNum)
    const viewport = page.getViewport({ scale: 1.5 })

    // Create canvas
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    canvas.width = viewport.width
    canvas.height = viewport.height

    // Render PDF page to canvas
    await page.render({
      canvasContext: context,
      viewport: viewport,
      canvas: canvas
    }).promise

    // Convert canvas to JPG at 75% quality
    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.75)
    const imageBytes = Uint8Array.from(atob(imageDataUrl.split(',')[1]), c => c.charCodeAt(0))

    // Embed JPG image in new PDF
    const jpgImage = await newPdfDoc.embedJpg(imageBytes)
    const pdfPage = newPdfDoc.addPage([viewport.width, viewport.height])
    pdfPage.drawImage(jpgImage, {
      x: 0,
      y: 0,
      width: viewport.width,
      height: viewport.height,
    })
  }

  // Save the compressed PDF
  const pdfBytes = await newPdfDoc.save()
  const blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' })

  return new File([blob], file.name, { type: 'application/pdf' })
}

async function splitCompressedPdf(file: File, originalName: string): Promise<File[]> {

  const files: File[] = []

  // If compressed PDF is still under limit, return it
  if (file.size <= MAX_SIZE) return [file]

  const buffer = await file.arrayBuffer()
  const pdfDoc = await PDFDocument.load(buffer)
  const totalPages = pdfDoc.getPageCount()

  let currentChunkPages: number[] = []
  let pageNum = 0

  while (pageNum < totalPages) {

    console.log(`Chuking PDF page ${pageNum + 1} / ${totalPages}`)

    currentChunkPages.push(pageNum)

    // Create a test chunk
    const testPdfDoc = await PDFDocument.create()
    const copiedPages = await testPdfDoc.copyPages(pdfDoc, currentChunkPages)
    copiedPages.forEach(page => testPdfDoc.addPage(page))
    const testBytes = await testPdfDoc.save()

    // If test chunk exceeds limit
    if (testBytes.byteLength > MAX_SIZE) {
      if (currentChunkPages.length === 1) {
        // Single page exceeds limit, include it anyway
        console.warn(`Single page ${pageNum + 1} exceeds size limit`)
        const chunkPdfDoc = await PDFDocument.create()
        const pages = await chunkPdfDoc.copyPages(pdfDoc, currentChunkPages)
        pages.forEach(page => chunkPdfDoc.addPage(page))
        const chunkBytes = await chunkPdfDoc.save()
        const blob = new Blob([new Uint8Array(chunkBytes)], { type: 'application/pdf' })
        const baseName = originalName.replace(/\.pdf$/i, '')
        files.push(new File([blob], `${baseName}_part${files.length + 1}.pdf`, { type: 'application/pdf' }))
        currentChunkPages = []
        pageNum++
      } else {
        // Remove last page and save chunk
        currentChunkPages.pop()
        const chunkPdfDoc = await PDFDocument.create()
        const pages = await chunkPdfDoc.copyPages(pdfDoc, currentChunkPages)
        pages.forEach(page => chunkPdfDoc.addPage(page))
        const chunkBytes = await chunkPdfDoc.save()
        const blob = new Blob([new Uint8Array(chunkBytes)], { type: 'application/pdf' })
        const baseName = originalName.replace(/\.pdf$/i, '')
        files.push(new File([blob], `${baseName}_part${files.length + 1}.pdf`, { type: 'application/pdf' }))
        currentChunkPages = []
      }
    } else {
      pageNum++
      // If we've reached the last page, save the chunk
      if (pageNum >= totalPages && currentChunkPages.length > 0) {
        const blob = new Blob([new Uint8Array(testBytes)], { type: 'application/pdf' })
        const baseName = originalName.replace(/\.pdf$/i, '')
        files.push(new File([blob], `${baseName}_part${files.length + 1}.pdf`, { type: 'application/pdf' }))
      }
    }
  }

  return files
}


